import { cn, Select } from '@nexo/ui';
import { Languages } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { currentLanguage, type Language, languages, setLanguage } from '../lib/i18n';

/**
 * Interface language picker. `compact` is the ES/EN toggle for headers (landing, sign in);
 * the default is a labelled select for settings.
 */
export function LanguageSwitcher({
  compact,
  className,
  onChange,
}: {
  compact?: boolean;
  className?: string;
  /** Called after switching, e.g. to save the choice on the user's account. */
  onChange?: (language: Language) => void;
}) {
  // useTranslation re-renders this on every language change.
  const { t } = useTranslation();
  const current = currentLanguage();

  const choose = (language: Language) => {
    setLanguage(language);
    onChange?.(language);
  };

  if (compact) {
    const next: Language = current === 'es' ? 'en' : 'es';
    return (
      <button
        type="button"
        onClick={() => choose(next)}
        title={t('Idioma')}
        aria-label={`${t('Idioma')}: ${languages[current]}`}
        className={cn(
          'inline-flex h-9 items-center gap-1.5 rounded-full px-3 text-sm font-medium uppercase transition hover:bg-black/5 dark:hover:bg-white/10',
          className,
        )}
      >
        <Languages className="size-4" />
        {current}
      </button>
    );
  }

  return (
    <Select
      aria-label={t('Idioma')}
      className={cn('w-44', className)}
      value={current}
      onChange={choose}
      icon={<Languages className="text-subtle" />}
      options={Object.entries(languages).map(([code, label]) => ({
        value: code as Language,
        label,
      }))}
    />
  );
}
